<script setup lang="ts">
import type { CollectionPage } from 'schema-dts'
import { useTopLevelLocale } from '~/composables/useTopLovelLocale'

const route = useRoute()

const tag = computed(() => Array.isArray(route.params.tag) ? route.params.tag[0] : route.params.tag)
const { topLevelLocale: locale } = useTopLevelLocale()

const { data, error } = await useFetch(`/internals/tag/${tag.value}`, {
  method: 'GET',
  lazy: false,
  query: {
    locale,
  },
})

if (error.value) {
  throw createError({ ...error.value, fatal: true })
}

const page = computed(() => queryValueToNumber(route.query.page) || 1)
const pageSize = 21 // 7 rows of 3 cards
const { data: dataList, status: statusList, error: errorList, refresh } = await useFetch(`/internals/tag/${tag.value}/list`, {
  method: 'GET',
  lazy: true,
  query: {
    locale,
    page,
    pageSize,
  },
})

const { isLoading } = useIsLoading(statusList)

watch(errorList, (value) => {
  if (value) {
    throw createError({ ...value, fatal: true })
  }
}, {
  immediate: true,
})

watch(page, () => {
  refresh()
  useScrollToTop()
}, {
  immediate: false,
})

const metaTitle = computed(() => useHeadTitle(data.value?.seo_title))
const metaImage = useSeoImageURL(data.value?.featured_image.url, { width: META_IMAGE_WIDTH, height: META_IMAGE_HEIGHT })
const requestURL = useRequestURL()
const canonicalHref = computed(() => getURLWithOnlySearchParams({ url: requestURL, searchParams: ['page'] }).href)

useSeoMeta({
  title: metaTitle,
})

useServerSeoMeta({
  ogTitle: metaTitle,
  twitterTitle: metaTitle,
  description: data.value?.description,
  ogDescription: data.value?.description,
  twitterDescription: data.value?.description,
  ogImage: metaImage,
  ogImageWidth: META_IMAGE_WIDTH,
  ogImageHeight: META_IMAGE_HEIGHT,
  twitterImage: metaImage,
  ogUrl: canonicalHref,
})

useHead({
  link: [
    { rel: 'canonical', href: canonicalHref },
  ],
  script: [
    getLdJsonScript<CollectionPage>({
      '@type': 'CollectionPage',
      'name': metaTitle.value,
      'description': data.value?.description,
      'url': canonicalHref.value,
    }),
  ],
})
</script>

<template>
  <div>
    <template v-if="data">
      <AppBreadcrumb
        :crumbs="data.breadcrumbs.slice(0, 1)"
        :current-title="data.title"
      />

      <AppHeaderPage
        :image="data.featured_image"
      >
        <template #title>
          {{ data.title }}
          <span
            v-if="isLoading"
            class="absolute top-1/2 -translate-y-1/2 ml-4"
          >
            <YscLoader class="h-8 w-8" />
          </span>
        </template>
      </AppHeaderPage>
    </template>

    <div class="container relative mb-8">
      <AppLoadingOverlay v-if="isLoading" />

      <template v-if="dataList">
        <AppPostCardList
          :posts="dataList.results"
          class="mb-12"
        />

        <div v-if="dataList.count > pageSize">
          <LazyYscPagination
            :page-size="pageSize"
            :count="dataList.count"
          />
        </div>
      </template>
    </div>
  </div>
</template>
